import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useLocation, type NavigateFunction } from 'react-router-dom'
import { type MenuItemWithSubItems } from '@dhl/page'
import { Dashboard, Home, MoreHorizontal, PieceMedium, Receipt, ServicePoint, Truck } from '@dhl/icons'
import { ParcelShopService } from '~/account/model/reducer'
import { selectIsLoggedIn, selectIsParcelShop, selectParcelShopServices } from '~/account/model/selectors'
import { links } from '~/links'
import { type ReduxState } from '~/redux/createRootReducer'

type MaybeMenuItem = MenuItemWithSubItems | boolean

const openServicePoint = (navigate: NavigateFunction) => {
  navigate(links.servicepointMapLink())
}

const useParcelShopMenuItems = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const { parcelShopServices } = useSelector(selectParcelShopServices)
  const originCountry = useSelector((state: ReduxState) => state.basket.session.originCountry)

  const showService = useCallback(
    (service: ParcelShopService) => parcelShopServices.includes(service),
    [parcelShopServices]
  )

  const parcelShopMenuItems = useMemo<MaybeMenuItem[]>(
    () => [
      {
        key: 'dashboard',
        label: t('dashboard'),
        icon: <Dashboard />,
        onClick: () => navigate(links.dashboardLink()),
        active: pathname.includes(links.dashboardLink()),
        testId: 'dashboard-button',
      },
      showService(ParcelShopService.OTC) && {
        key: 'shipment',
        label: t('shipment'),
        icon: <PieceMedium />,
        onClick: () => navigate(links.shipmentLink()),
        active: pathname.includes(links.shipmentLink()),
        testId: 'shipment-button',
      },
      {
        key: 'parcelshoplocator',
        label: t('parcelshoplocator'),
        icon: <ServicePoint />,
        onClick: () => openServicePoint(navigate),
        active: pathname.includes(links.servicepointMapLink()),
        testId: 'parcelshoplocator-button',
      },
      {
        key: 'report',
        label: t('report'),
        icon: <Receipt />,
        onClick: () => navigate(links.reportLink()),
        active: pathname.includes(links.reportLink()),
        testId: 'report-button',
      },
      originCountry === 'NL' && {
        key: 'spa',
        label: t('spa'),
        icon: <Home />,
        onClick: () => navigate(links.spaLink()),
        active: pathname.includes(links.spaLink()),
        testId: 'spa-button',
      },
    ],
    [navigate, originCountry, pathname, showService, t]
  )

  return parcelShopMenuItems
}

const usePwaMenuItems = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const { isLoggedIn } = useSelector(selectIsLoggedIn)

  const getContextualLink = useCallback(
    (portalLink: string, onboardingLink: string) => {
      if (isLoggedIn) {
        return portalLink
      }
      return onboardingLink
    },
    [isLoggedIn]
  )

  const pwaMenuItems = useMemo<MaybeMenuItem[]>(
    () =>
      [
        {
          key: 'dashboard',
          label: t('dashboard'),
          icon: <Dashboard />,
          onClick: () => navigate(getContextualLink(links.portalDashboardLink(), links.onboardingDashboardLink())),
          testId: 'dashboard-button',
          active: pathname.includes(links.dashboardLink()),
        },
        {
          key: 'receive',
          label: t('portal-receive'),
          icon: <Truck />,
          onClick: () =>
            navigate(getContextualLink(links.portalIncomingPiecesLink(), links.onboardingIncomingPiecesLink())),
          testId: 'bottom-nav-receive',
          active: false,
        },
        {
          key: 'send',
          label: t('portal-send'),
          icon: <PieceMedium />,
          onClick: () =>
            navigate(getContextualLink(links.portalOutgoingPiecesLink(), links.onboardingOutgoingPiecesLink())),
          testId: 'bottom-nav-send',
          active: pathname.includes(links.shipmentLink()),
        },
        {
          key: 'more',
          label: t('more'),
          icon: <MoreHorizontal />,
          onClick: () => navigate(getContextualLink(links.portalMoreLink(), links.onboardingMoreLink())),
          active: pathname.includes(links.homeMoreLink()),
          testId: 'bottom-nav-more',
        },
      ].filter(Boolean),
    [getContextualLink, navigate, pathname, t]
  )

  return pwaMenuItems
}

export const useMenuItems = () => {
  const { isParcelShop } = useSelector(selectIsParcelShop)
  const parcelShopMenuItems = useParcelShopMenuItems()
  const pwaMenuItems = usePwaMenuItems()

  const menuItems = isParcelShop ? parcelShopMenuItems : pwaMenuItems

  return menuItems.filter(Boolean) as MenuItemWithSubItems[]
}
